import { appStorage, appSession } from '../lib/safeStorage';
import React, { useState } from 'react';
import { Bell, BellOff, BellRing, X, Loader2, Check } from 'lucide-react';
import { usePushNotifications } from '../hooks/usePushNotifications';

/**
 * Floating notification prompt for users who haven't enabled push notifications.
 * Shows after a delay for logged-in users.
 */
export default function NotificationPrompt() {
  const { 
    isSupported, 
    isSubscribed, 
    permission, 
    subscribe, 
    loading 
  } = usePushNotifications();
  
  const [showPrompt, setShowPrompt] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [justEnabled, setJustEnabled] = useState(false);

  // Show prompt after delay for eligible users
  React.useEffect(() => {
    // Check if already dismissed this session
    if (appSession.getItem('notificationPromptDismissed')) {
      setDismissed(true);
      return;
    }

    // Only show if supported, not subscribed, and permission not denied
    if (!isSupported || isSubscribed || permission === 'denied') {
      return;
    }

    // Show after 10 seconds (give user time to settle)
    const timer = setTimeout(() => {
      // Also check if user is logged in
      const raw = appStorage.getItem('user') || appSession.getItem('user');
      if (raw && !dismissed) {
        // Shift reminders are a crew/staff thing — owners/admins ("manager")
        // don't work shifts, so the prompt must never reach them.
        let role = '';
        try { role = (JSON.parse(raw) || {}).role || ''; } catch { role = ''; }
        if (role === 'manager') return;
        setShowPrompt(true);
      }
    }, 10000);

    return () => clearTimeout(timer);
  }, [isSupported, isSubscribed, permission, dismissed]);

  const handleEnable = async () => {
    const success = await subscribe();
    if (success) {
      setJustEnabled(true);
      setTimeout(() => {
        setShowPrompt(false);
        appSession.setItem('notificationPromptDismissed', 'true');
      }, 2000);
    }
  };

  const handleDismiss = () => {
    setShowPrompt(false);
    setDismissed(true);
    appSession.setItem('notificationPromptDismissed', 'true');
  };

  // Don't render if not showing or already subscribed
  if (!showPrompt || isSubscribed || !isSupported || permission === 'denied') {
    return null;
  }

  return (
    <div 
      className="fixed bottom-40 left-4 right-4 sm:left-auto sm:right-4 sm:w-72 z-40 animate-in slide-in-from-bottom duration-300"
      data-testid="notification-prompt"
    >
      <div className="bg-gradient-to-br from-[#1A1A2E] to-[#252540] rounded-xl border border-white/10 shadow-xl overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-3 border-b border-white/10">
          <div className="flex items-center gap-2">
            <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
              justEnabled 
                ? 'bg-emerald-500/20' 
                : 'bg-gradient-to-br from-[#D4AF37] to-[#E5C76B]'
            }`}>
              {justEnabled ? (
                <Check className="w-4 h-4 text-emerald-400" />
              ) : (
                <BellRing className="w-4 h-4 text-[#0B0F1A]" />
              )}
            </div>
            <div>
              <h3 className="text-white text-sm font-semibold">
                {justEnabled ? 'Notifications Enabled!' : 'Never Miss a Shift'}
              </h3>
              <p className="text-gray-400 text-xs">
                {justEnabled ? 'You\'ll be notified of updates' : 'Enable shift reminders'}
              </p>
            </div>
          </div>
          {!justEnabled && (
            <button
              onClick={handleDismiss}
              className="p-1 hover:bg-white/10 rounded-lg transition-colors"
              aria-label="Dismiss"
            >
              <X className="w-4 h-4 text-gray-400" />
            </button>
          )}
        </div>

        {/* Content */}
        {!justEnabled && (
          <div className="p-3 space-y-2">
            <p className="text-gray-400 text-xs">
              Get reminders 3 days and 1 day before your events.
            </p>
            <div className="flex gap-2">
              <button
                onClick={handleDismiss}
                className="flex-1 min-h-[44px] py-2 px-3 text-gray-400 hover:text-white hover:bg-white/10 rounded-lg text-xs transition-colors"
              >
                Not Now
              </button>
              <button
                onClick={handleEnable}
                disabled={loading}
                className="flex-1 flex items-center justify-center gap-1.5 min-h-[44px] py-2 px-3 bg-gradient-to-r from-[#D4AF37] to-[#E5C76B] text-[#0B0F1A] rounded-lg font-semibold text-xs hover:opacity-90 transition-opacity disabled:opacity-50"
                data-testid="enable-notifications-btn"
              >
                {loading ? (
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                ) : (
                  <>
                    <Bell className="w-3.5 h-3.5" />
                    Enable
                  </>
                )}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
